import { StrictMode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ForbiddenError } from '@bmwe/data';
import { addLocale, PrimeReactProvider } from 'primereact/api';
import Layout, { LayoutProps } from '../layout/layout';
import {
  EntityRoutesMapProvider,
  EntityRoutesRecord,
  ToastProvider,
} from '../../context';
import { de } from 'primelocale/js/de.js';
import { PrimeReactOverlayRepaintPatch } from '../../components/repaintOverlay/repaintOverlay';
import { ConfirmDialog } from 'primereact/confirmdialog';

// Register the de locale at module load so PrimeReact components that render
// during the first commit (e.g. Calendar) can look up their localized labels.
// Doing this in useMountEffect would run after first render and trigger
// "chooseDate option is not found in the current locale('de')".
addLocale('de', de);

export type RootProps = LayoutProps & {
  entityRoutes?: EntityRoutesRecord;
  stickyErrorToasts?: boolean;
};

export function Root({
  user,
  items,
  entityRoutes = {},
  stickyErrorToasts = false,
}: RootProps) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        throwOnError: (error) => error instanceof ForbiddenError,
        retry: (failureCount, error) => {
          if (error instanceof ForbiddenError) return false;
          return failureCount < 3;
        },
      },
    },
  });
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <PrimeReactProvider value={{ locale: 'de' }}>
          <ToastProvider stickyErrors={stickyErrorToasts}>
            <EntityRoutesMapProvider routes={entityRoutes}>
              <Layout user={user} items={items} />
              <ConfirmDialog />
              <PrimeReactOverlayRepaintPatch />
            </EntityRoutesMapProvider>
          </ToastProvider>
        </PrimeReactProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

export default Root;
